import { Action, Reducer } from "redux";
import { AppThunkAction, VehicleIsValidAction, VEHICLE_IS_VALID, remoteNOActions } from ".";
import { TextInputState } from "../interfaces";

export const REG_NO_QUESTION_CHANGED = "REG_NO_QUESTION_CHANGED";
export const REG_NO_CHANGED = "REG_NO_CHANGED";
export const VIN_NO_CHANGED = "VIN_NO_CHANGED";

export interface VehicleState {
  VINNo: TextInputState;
  regNo: TextInputState;
  regNoQuestion: RegNoQuestion;
  isValid: boolean;
}

export interface RegNoQuestion {
  title?: string;
  selected?: boolean;
  values: RegNoValue[];
  description?: { title: string; text: string };
  requiredErrorMessage?: string;
  showError?: boolean;
  help?: { title?: string; description?: string };
}

export interface RegNoValue {
  value: boolean;
  text: string;
}

const defaults = {
  regNoQuestion: {
    title: "Har bilen registreringsnummer?",
    selected: false,
    values: [
      {
        value: true,
        text: "Ja",
      },
      {
        value: false,
        text: "Nej",
      },
    ],
    requiredErrorMessage: "error",
    showError: false,
    help: {
      title: "help title",
      description: "help description",
    },
  },
  regNo: {
    title: "Skriv inn registreringsnummeret",
    state: 0,
    stateDescriptions: ["Default", "NotSet", "Incorrect"],
    secureValue: "",
    value: "",
  },
  VINNo: {
    title: "Skriv inn de ni siste tegnene i chassisnummeret",
    state: 0,
    stateDescriptions: ["Default", "NotSet", "Incorrect"],
    secureValue: "",
    value: "",
  },
  isValid: true,
};

export type ChangeAction = {
  type: typeof REG_NO_QUESTION_CHANGED | typeof REG_NO_CHANGED | typeof VIN_NO_CHANGED;

  payload: { value: any };
};

export const reducer: Reducer<VehicleState> = (state: VehicleState = defaults, input: Action | VehicleIsValidAction): VehicleState => {
  const action: ChangeAction | VehicleIsValidAction = input as ValidAction;

  switch (action?.type) {
    case REG_NO_QUESTION_CHANGED:
      return {
        ...state,
        regNoQuestion: {
          ...state.regNoQuestion,
          selected: action.payload.value,
        },
      };
    case REG_NO_CHANGED:
      const regNoValue = action.payload.value ?? "";
      if (state.regNo.value === regNoValue) {
        return state;
      }
      return {
        ...state,
        regNo: {
          ...state.regNo,
          value: regNoValue,
          state: getInputState(regNoValue, RegNoRegEx),
        },
      };
    case VIN_NO_CHANGED:
      const VINNoValue = action.payload.value ?? "";
      if (state.regNo.value === VINNoValue) {
        return state;
      }
      return {
        ...state,
        VINNo: {
          ...state.VINNo,
          value: VINNoValue,
          state: getInputState(VINNoValue, VINNoRegEx),
        },
      };
    case VEHICLE_IS_VALID: {
      return isValid(state);
    }
    default:
      return state;
  }
};

type ValidAction = ChangeAction & VehicleIsValidAction;

const VINNoRegEx: RegExp = /^[0-9A-HJ-NPR-Z]{9}$/;
const RegNoRegEx: RegExp = /^[A-Z]{2}\d{5}$/;

const getInputState = (value: string, pattern: RegExp) => (value.length > 0 ? (pattern.test(value) ? 0 : 2) : 1);

const isValid = (state: VehicleState): VehicleState => {
  const VINNoState = getInputState(state.VINNo.value ?? "", VINNoRegEx);
  const RegNoState = getInputState(state.regNo.value ?? "", RegNoRegEx);

  const isValid = (!state.regNoQuestion.selected && VINNoState === 0) || (state.regNoQuestion.selected && RegNoState === 0);

  if (state.regNo.state === RegNoState && state.VINNo.state === VINNoState && state.isValid === isValid) {
    return state;
  }

  return {
    ...state,
    regNo: {
      ...state.regNo,
      state: RegNoState,
    },
    VINNo: {
      ...state.VINNo,
      state: VINNoState,
    },
    isValid: isValid ?? true,
  };
};

export const actions = {
  RegNoQuestionChanged:
    (value: unknown): AppThunkAction<ValidAction | {}> =>
      dispatch => {
        dispatch({
          type: REG_NO_QUESTION_CHANGED,
          payload: { value: value as boolean },
        });
      },
  RegNoChanged:
    (value: unknown): AppThunkAction<ValidAction | {}> =>
      dispatch => {
        dispatch({
          type: REG_NO_CHANGED,
          payload: { value: value as string },
        });
      },
  VINNoChanged:
    (value: unknown): AppThunkAction<ValidAction | {}> =>
      dispatch => {
        dispatch({
          type: VIN_NO_CHANGED,
          payload: { value: value as string },
        });
        dispatch(remoteNOActions.putChassisNumberToQuote());
      },
};
